import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { MetadataDefinitionGridModel } from "../../types/MetadataDefinitionGridModel";
import {
  GetDownloadFiles,
  GetEditMetadataOrderdefinition,
  SaveDocViewDetails,
} from "../../api/preworkDetailApi/preworkDetailApi";
import { Col, Form, Row } from "react-bootstrap";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forEach } from "lodash";
import { FieldsModel } from "../../types/FieldsModel";
import LocalStorageUtil from "../../utils/localStorage";

interface DocviewPopupProps {
  isOpen: boolean;
  onClose: (isCancelClick: boolean) => void;
  title: string;
  selectedData: any;
}

const DocviewPopup: React.FC<DocviewPopupProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [, setValidationMessage] = useState<string | null>(null); // NOSONAR
  const [projectId, setProjectId] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [projectManager, setProjectManager] = useState("");
  const [supplierTrackingId, setSupplierTrackingId] = useState("");
  const [workOrderSubmittedDate, setWorkOrderSubmittedDate] = useState("");
  const [supplierDueDate, setSupplierDueDate] = useState("");
  const [comment, setComment] = useState("");
  const [workArea, setWorkArea] = useState("");
  const [errors, setErrors] = useState<{
    workAreaError: string | null;
    projectManagerError: string | null;
  }>({
    workAreaError: null,
    projectManagerError: null,
  });
  const [responseData, setResponseData] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      getData();
      setRowData(selectedData.ChildList);
    }
  }, [isOpen]);

  // Custom cell renderer for clickable file name
  const fileNameCellRenderer = (params: any) => {
    return (
      <button
        type="button"
        onClick={() => handleFileClick(params)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--aggrid-text-color)",
          textDecoration: "underline",
          textAlign: "left",
        }}
      >
        {params.value}
      </button>
    );
  };

  const handleFileClick = async (childDocData: any) => {
    try {
      setLoading(true);
      const params: any[] = [
        {
          DocumentStreamID: childDocData.data.childFileDocumentStreamID,
          AccountId: Number(LocalStorageUtil.getItem("accountId")),
          fileName: childDocData.data.childFileName,
          contextName: LocalStorageUtil.getItem("contextName"),
        },
      ];
      const response = await GetDownloadFiles(params);
      if (response.status === 200) {
        const data = response.data;
        const contentType = response.headers["content-type"];
        const fileName = childDocData.data.childFileName;
        const linkElement = document.createElement("a");
        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        linkElement.href = url;
        linkElement.setAttribute("download", fileName);
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: false,
        });
        linkElement.dispatchEvent(clickEvent);
      }
    } catch (error) {
      console.log("Error in handleFileClick ", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Defined grid column
  const [columnDef] = useState<ColDef[]>([
    {
      headerName: "File Name",
      field: "childFileName",
      sortable: true,
      width: 400,
      filter: true,
      resizable: true,
      cellRenderer: fileNameCellRenderer,
    },
    {
      headerName: "File Size",
      field: "fileSize",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
    },
  ]);

  //Close the dialog box
  const handleCloseClick = () => {
    onClose(true);
  };

  //Handle Work Area change
  const handleWorkAreaChange = (event: any) => {
    const { value } = event.target;
    setWorkArea(value);
    setErrors({ ...errors, workAreaError: null });
  };

  //Handle Project Manager change
  const onChangeProjectManeger = (event: any) => {
    if (event.target.value.length > 0) {
      setErrors({ ...errors, projectManagerError: null });
      setProjectManager(event.target.value);
    } else {
      setErrors({
        ...errors,
        projectManagerError: "Project Manager is required",
      });
    }
  };

  const getFieldData = (field: string) => {
    switch (field) {
      case "Project ID":
        return projectId;
      case "Project Description":
        return projectDescription;
      case "Project Manager":
        return projectManager;
      case "Supplier Tracking ID":
        return supplierTrackingId;
      case "Work Order Submitted Date":
        return workOrderSubmittedDate;
      case "Supplier Due Date":
        return supplierDueDate;
      case "Comments":
        return comment;
      case "Work Area":
        return workArea;
      default:
        return "";
    }
  };
  //Save and Submit button click
  const handleSaveAndSubmitClick = async () => {
    try {
      setLoading(true);
      let fields: FieldsModel[] = [];
      forEach(responseData, (item) => {
        const params: FieldsModel = {
          MappingId: item.mappingAutoId,
          Data: getFieldData(item.field),
          Name: item.field,
          DocumentId: selectedData.parentDocumentID,
          ContextName: LocalStorageUtil.getItem("contextName"),
        };
        fields.push(params);
      });
      const response = await SaveDocViewDetails(fields);
      if (response) {
        setValidationMessage("Data saved successfully");
        onClose(false);
      }
    } catch (error) {
      console.error("Error in DocviewPopup ", error);
      throw error;
    }
  };

  // Data fetch from API
  const getData = async () => {
    try {
      setLoading(true);
      const params: MetadataDefinitionGridModel = {
        MetadataDefinitionGrid: undefined,
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        SearchText: "",
        IsSuperAdmin: 0,
        CreatedDate: new Date(),
        CreatedBy: Number(LocalStorageUtil.getItem("userId")),
        UpdatedDate: new Date(),
        UpdatedBy: "",
        MetadataDefinitionId: Number(
          LocalStorageUtil.getItem("metadatadefinitionId")
        ),
        MetadataDefinitionName: "",
        MetadataOrderDefinitionId: 0,
        MappingId: 0,
        MappingAutoId: 0,
        Field: "",
        OrderBy: 0,
        MetadatadefinitionselectedItemId: 0,
        FieldName: "",
        IsDisabled: false,
        IsCustomizable: false,
        Type: "",
        DefaultValue: "",
        Data: "",
        DocumentId: selectedData.parentDocumentID,
        DocumentStreamId: selectedData.childFileDocumentStreamID,
        ReadOnly: false,
        ContextName: LocalStorageUtil.getItem("contextName"),
      };
      const response = await GetEditMetadataOrderdefinition(params);
      if (response) {
        setResponseData(response);
        forEach(response, (item) => {
          switch (item.field) {
            case "Project ID":
              setProjectId(item.data);
              break;
            case "Project Description":
              setProjectDescription(item.data);
              break;
            case "Project Manager":
              setProjectManager(item.data);
              break;
            case "Supplier Tracking ID":
              setSupplierTrackingId(item.data);
              break;
            case "Work Order Submitted Date":
              if (item.data !== "") {
                const workOrerDate = new Date(item.data);
                const workOrderFormattedDate = workOrerDate
                  .toISOString()
                  .split("T")[0];
                setWorkOrderSubmittedDate(workOrderFormattedDate);
              } else {
                setWorkOrderSubmittedDate("");
              }
              break;
            case "Supplier Due Date":
              if (item.data !== "") {
                const supplierDueDate = new Date(item.data);
                const supplierDueDateFormattedDate = supplierDueDate
                  .toISOString()
                  .split("T")[0];
                setSupplierDueDate(supplierDueDateFormattedDate);
              } else {
                setSupplierDueDate("");
              }
              break;
            case "Comments":
              setComment(item.data);
              break;
            case "Work Area":
              setWorkArea(item.data);
              break;
            default:
              break;
          }
        });
        setProjectId(response[0].data);
      }
    } catch (error) {
      console.error("Error in getData ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierDueDate(event.target.value);
  };

  //Return the UI
  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseClick}
        maxWidth="sm"
        PaperProps={{
          style: {
            height: "90.8%",
            width: "90%",
          },
        }}
      >
        <DialogTitle style={{ backgroundColor: "var(--primary-color)" }}>
          <div
            className="col-sm-12"
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            }}
          >
            <div
              className="col-sm-11"
              style={{
                display: "flex",
                alignItems: "left",
                gap: "10px",
                color: "var(--aggrid-text-color)",
              }}
            >
              {title}
            </div>
            <div
              className="col-sm-1"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "right",
              }}
            >
              {" "}
              <Tooltip title="Close">
                <IconButton
                  onClick={() => onClose(true)}
                  style={{
                    cursor: "pointer",
                    alignItems: "flex-end",
                    color: "var(--aggrid-text-color)",
                    width: "20px",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="row">
              <div className="rp-main-inner full-width100">
                <div className="form-inp col-sm-12">
                  <h4 className="form-heading" style={{ fontSize: 12 }}>
                    DOCUMENT MANAGEMENT FORM
                  </h4>
                </div>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col md={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Project ID
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Project ID"
                        name="projectId"
                        value={projectId}
                        onChange={(e) => setProjectId(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Project Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        placeholder="Enter Project Description"
                        name="projectDescription"
                        rows={3}
                        value={projectDescription}
                        onChange={(e) => setProjectDescription(e.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.projectManagerError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col md={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Project Manager *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Project Manager"
                        name="projectManager"
                        required
                        value={projectManager}
                        onChange={onChangeProjectManeger}
                        isInvalid={!!errors.projectManagerError}
                        disabled={true}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.projectManagerError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col ms={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Supplier Tracking ID
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Supplier ID"
                        name="supplierTrackingId"
                        value={supplierTrackingId}
                        onChange={(e) => setSupplierTrackingId(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col ms={3}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Work Order Submitted Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        placeholder="Enter Supplier Name"
                        name="workOrderSubmittedDate"
                        value={workOrderSubmittedDate}
                        onChange={(e) =>
                          setWorkOrderSubmittedDate(e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col ms={3}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Supplier Due Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        placeholder="Enter Supplier Name"
                        name="supplierDueDate"
                        value={supplierDueDate}
                        onChange={handleDateChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col md={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">Comment</Form.Label>
                      <Form.Control
                        as="textarea"
                        placeholder="Enter comment"
                        name="comment"
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <h4 className="form-heading" style={{ fontSize: 12 }}>
                  ENTER INPUT PARAMETERS
                </h4>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col md={6}>
                    <Form.Group controlId="formGroupName">
                      <Form.Label className="input-labels">
                        Work Area *
                      </Form.Label>
                      <Form.Select
                        value={workArea}
                        onChange={handleWorkAreaChange}
                        isInvalid={!!errors.workAreaError}
                        disabled={true}
                      >
                        <option value={1}>{workArea}</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.workAreaError}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3" style={{ fontSize: 12 }}>
                  <Col md={12}>
                    {/* Ag-Grid */}
                    <div className="form-inp col-sm-12">
                      <div
                        className="ag-theme-quartz-dark"
                        style={{
                          height: "20vh",
                          width: "100%",
                          marginTop: "10px",
                        }}
                      >
                        <AgGridReact
                          rowData={rowData}
                          columnDefs={columnDef}
                          headerHeight={25}
                          gridOptions={{
                            rowSelection: {
                              mode: "multiRow",
                              enableClickSelection: false,
                            },
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSaveAndSubmitClick}
            style={{
              textTransform: "none",
              borderRadius: "0px",
              backgroundColor: "var(--button-background-color)",
              color: "var(--button-color)",
              fontSize: "11px",
              padding: "5px 12px",
              border: "1px solid #144979",
              margin: "0 5px 20px 5px",
              whiteSpace: "white-space",
            }}
          >
            Save & Submit
          </Button>
          <Button
            style={{
              textTransform: "none",
              borderRadius: "0px",
              backgroundColor: "var(--button-background-color)",
              color: "var(--button-color)",
              fontSize: "11px",
              padding: "5px 12px",
              border: "1px solid #144979",
              margin: "0 20px 20px 5px",
              whiteSpace: "white-space",
            }}
            onClick={handleCloseClick}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DocviewPopup;
